import _ from 'lodash';
import { BaseItemStore } from '@/trendData/baseItem.store';
import {
  DASH_STYLES,
  EMPTY_STRING_VALUE,
  ITEM_DATA_STATUS,
  ITEM_TYPES,
  Y_AXIS_TYPES,
} from '@/trendData/trendData.constants';
import { getYAxisConfig } from '@/trendData/yAxis.actions';
import type { Item } from '@/trendData/baseItem.store';
import type { DehydratedScalarItem, ScalarItem } from '@/trendData/trendData.types';

export const NON_NUMERICAL_SCALAR_CHART_VALUE = 1;

interface ScalarAddPayload {
  id: string;
  name: string;
  lane: number;
  alignment: string;
  color?: string;
  laneReservation?: string;
}

interface ScalarValuePayload {
  id: string;
  value?: number | string | boolean;
  lower?: number;
  upper?: number;
  start: number;
  end: number;
  warningCount?: number;
  warningLogs?: unknown;
  timingInformation?: string;
  meterInformation?: string;
  valueUnitOfMeasure?: string;
}

export type ScalarDehydratedState = Pick<BaseItemStore<Item>, 'items'> & {
  items: DehydratedScalarItem[];
};

type ScalarItemRuntime = ScalarItem & {
  stringEnum?: Array<{ key: number; stringValue: string }>;
  pointValueLower?: number;
  pointValueUpper?: number;
};

type ScalarItemUpdate = Partial<
  Omit<ScalarItemRuntime, 'value' | 'pointValue' | 'warningLogs' | 'valueUnitOfMeasure'>
> & {
  value?: number | string | boolean | null;
  pointValue?: number | string | boolean | null;
  warningLogs?: unknown[];
  valueUnitOfMeasure?: string;
  pointValueLower?: number;
  pointValueUpper?: number;
  stringEnum?: Array<{ key: number; stringValue: string }>;
};

/**
 * A store for containing scalars which can be displayed on the chart. A scalar is composed of a single y-axis value.
 *
 * This store is augmented with additional functionality from sqBaseSignalStore.
 */

export class TrendScalarStore extends BaseItemStore<ScalarItem> {
  static readonly storeName = 'sqTrendScalarStore';

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): ScalarDehydratedState {
    return {
      items: _.chain(this.state.get('items'))
        .filter(this.shouldDehydrateItem)
        .map((item) => this.pruneDehydratedItemProps(item))
        .value() as DehydratedScalarItem[],
    };
  }

  /**
   * Re-creates the scalars. All necessary data needed to rehydrate is persisted so no actions have to be called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   * @return {Object} A promise that is fulfilled when items are completely rehydrated.
   */
  rehydrate(dehydratedState: ScalarDehydratedState): void {
    this.state.set(
      'items',
      _.map(dehydratedState.items, (item) =>
        this.createScalar(
          item.id,
          item.name,
          item.lane,
          item.axisAlign,
          _.omit(item, ['id', 'name', 'lane', 'axisAlign']) as ScalarItemUpdate,
        ),
      ),
    );
  }

  /**
   * Adds a scalar item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the new item
   * @param {String} payload.name - Name of the new item
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} [payload.color] - Color hex code (e.g. #CCCCCC)
   */
  addScalar(payload: ScalarAddPayload): void {
    this.state.push(
      'items',
      this.createScalar(
        payload.id,
        payload.name,
        payload.lane,
        payload.alignment,
        _.pick(payload, ['color', 'laneReservation']),
      ),
    );
  }

  /**
   * Adds additional data to an item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the item
   * @param {Number} payload.value - Value of the scalar
   * @param {Number} payload.lower - the lower bound if this scalar has a shadedArea
   * @param {Number} payload.upper - the upper bound if this scalar has a shadedArea
   * @param {Number} payload.start - the start time of the scalar. Normally the start of the display range. Used to
   * show labels for the scalar
   * @param {Number} payload.end - the end time of the scalar
   * @param {Number} payload.warningCount - the count of warnings that will be passed to the base item store
   * @param {Object[]} payload.warningLogs - the log of warnings that will be passed to the base item store
   */
  addValue(payload: ScalarValuePayload): void {
    const isStringSeries = _.isString(payload.value) || _.isBoolean(payload.value);
    const cursor = this.getItemCursor(payload.id);
    const normalizedWarningLogs =
      payload.warningLogs === undefined
        ? undefined
        : Array.isArray(payload.warningLogs)
          ? payload.warningLogs
          : [payload.warningLogs];
    if (cursor.exists()) {
      this.setDataStatusTo(ITEM_DATA_STATUS.PRESENT, {
        id: payload.id,
        warningCount: payload.warningCount,
        warningLogs: normalizedWarningLogs,
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
      });

      const baseUpdate: ScalarItemUpdate = {
        start: payload.start,
        end: payload.end,
      };

      if (payload.valueUnitOfMeasure !== undefined) {
        baseUpdate.valueUnitOfMeasure = payload.valueUnitOfMeasure;
      }

      if (payload.warningCount !== undefined) {
        baseUpdate.warningCount = payload.warningCount;
      }
      if (normalizedWarningLogs !== undefined) {
        baseUpdate.warningLogs = normalizedWarningLogs;
      }
      if (payload.timingInformation !== undefined) {
        baseUpdate.timingInformation = payload.timingInformation;
      }
      if (payload.meterInformation !== undefined) {
        baseUpdate.meterInformation = payload.meterInformation;
      }

      if (isStringSeries || _.isFinite(payload.value as number)) {
        const value = isStringSeries ? NON_NUMERICAL_SCALAR_CHART_VALUE : (payload.value as number);
        baseUpdate.pointValue = (payload.value as number | string | boolean) ?? null;
        baseUpdate.value = (payload.value as number | string | boolean) ?? null;
        baseUpdate.isStringSeries = isStringSeries;
        baseUpdate.data = [
          [payload.start, value],
          [payload.end, value],
        ];
      } else if (
        _.isFinite(payload.lower as number) &&
        _.isFinite(payload.upper as number) &&
        (payload.lower as number) < (payload.upper as number)
      ) {
        const lower = payload.lower as number;
        const upper = payload.upper as number;
        baseUpdate.pointValueLower = lower;
        baseUpdate.pointValueUpper = upper;
        baseUpdate.isStringSeries = false;
        baseUpdate.lower = lower;
        baseUpdate.upper = upper;
        baseUpdate.data = [
          [payload.start, lower, upper],
          [payload.end, lower, upper],
        ];
      } else {
        baseUpdate.isStringSeries = false;
        baseUpdate.data = [];
      }

      const mergedPayload = _.assign({}, getYAxisConfig(payload.id), baseUpdate) as ScalarItemUpdate;
      cursor.merge(mergedPayload);
      if (isStringSeries) {
        cursor.set('stringEnum', [
          {
            key: NON_NUMERICAL_SCALAR_CHART_VALUE,
            stringValue: payload.value === '' ? EMPTY_STRING_VALUE : String(payload.value),
          },
        ]);
      }
    }
  }

  /**
   * Private helper function to add a scalar to the store using the specified properties.
   *
   * @param {String} id - ID to use for the new scalar
   * @param {String} name - Name to use for the new scalar
   * @param {String} lane - Lane to use for the new series
   * @param {String} alignment - Alignment to use for the new series
   * @param {Object} props - Object containing properties to apply to the new scalar
   * @returns {Object} Newly created scalar object.
   */
  createScalar(id: string, name: string, lane: number, alignment: string, props: ScalarItemUpdate): ScalarItem {
    return this.createItem(
      id,
      name,
      ITEM_TYPES.SCALAR,
      _.assign(
        {
          data: [],
          dashStyle: DASH_STYLES.DASH,
          isStringSeries: false,
          yAxisConfig: {},
          statistics: {},
          lane,
          axisAlign: alignment,
          axisVisibility: true,
          axisAutoScale: true,
          visible: true,
          lineWidth: 1,
          yAxisType: Y_AXIS_TYPES.LINEAR,
          dataStatus: ITEM_DATA_STATUS.INITIALIZING,
          statusMessage: '',
        },
        props,
      ),
    ) as ScalarItem;
  }

  private readonly localHandlers = {
    TREND_ADD_SCALAR: (payload: ScalarAddPayload) => this.addScalar(payload),
    TREND_SCALAR_RESULTS_SUCCESS: (payload: ScalarValuePayload) => this.addValue(payload),
  };

  protected readonly handlers = { ...this.localHandlers, ...this.baseHandlers };
}
